import { Link, useParams } from 'react-router-dom';
import amsLogo from 'assets/images/agrologo.jpg';
import amsFavIcon from 'assets/images/agro-favicon.png';
import Avatar from 'components/Avatar';
import { useState } from 'react';
import ROUTES from 'constants/routes';
import useUser from 'hooks/useUser';
import capitalizeWords from 'utils/capitalizeWords';
import { signOut } from 'firebase/auth';
import {
    auth,
    db,
    fetchDocument,
    fetchCollectionByQueryLocal,
    fetchCollectionToRedux,
    getFromStorage,
    modifyAuthError,
    removeFromStorage,
    saveToStorage,
    setUser,
} from 'utils';
import { toast } from 'react-toastify';
import Table from 'components/Tables/containers/Table';
import TableItem from 'components/Tables/containers/TableItem';
import NotFound from 'components/NotFound';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'components/Modal';
import { useEffect } from 'react';
import { setHubs, setUsers } from 'redux/slices/app';
import Loader from 'components/Loader';
import Dropdown from 'components/Dropdown';
import { DASHBOARD_TYPES, DEPARTMENTS, HUB_ROLES, LOCAL_STORAGE_KEYS } from 'constants/global';
import { collection, doc, getDoc, query, serverTimestamp, where, writeBatch, setDoc } from 'firebase/firestore';
import { formatDateValues } from 'utils/formatDateValues';
import capitalizeFirstLetter from 'utils/capitalizeFirstLetter';
import { convertFromSlug, convertToSlug } from 'utils/slug';
import formatCurrency from 'utils/formatCurrency';

export const Nav = ({ sideBarOpen, setSideBarOpen }) => {
    const { hub } = useParams();
    const { user, isSuperAdmin, isFinance } = useUser();
    const [openProfile, setOpenProfile] = useState(false);
    const [openRoleModal, setOpenRoleModal] = useState(false);
    const [openNotification, setOpenNotification] = useState(false);
    const [openLogisticsRate, setOpenLogisticsRate] = useState(false);
    const [logisticsRate, setLogisticsRate] = useState();
    const hubUser = user?.role === HUB_ROLES.AGENT || user?.role === HUB_ROLES.MANAGER;
    const dashboardType = getFromStorage(LOCAL_STORAGE_KEYS.DASHBOARD_TYPE);

    const logout = async () => {
        try {
            await signOut(auth);
            removeFromStorage(LOCAL_STORAGE_KEYS.STORAGE_LOGS_DATA);
            setUser(null);
        } catch (error) {
            toast.error(modifyAuthError(error?.code));
        }
    };

    useEffect(() => {
        if (isSuperAdmin && dashboardType === DASHBOARD_TYPES.LOGISTICS) {
            fetchDocument('logisticsRate', 'logisticsRate', () => {}, setLogisticsRate);
        }
    }, [isSuperAdmin, dashboardType]);

    return (
        <>
            <nav
                className={`bg-white border-b h-[58px] grid transition-all duration-300 ${
                    sideBarOpen ? 'grid-cols-[230px_1fr]' : 'grid-cols-[75px_1fr]'
                }`}
            >
                <div
                    className={`border-r h-full flex items-center relative bg-white ${
                        sideBarOpen
                            ? 'max-sm:fixed max-sm:!h-[58px] max-sm:border-b !left-0 !top-0 max-475:w-[200px] max-sm:w-[230px] max-sm:!z-[1200] px-5'
                            : 'justify-center pr-2'
                    }`}
                >
                    <Link to={'/'}>
                        {sideBarOpen ? (
                            <img src={amsLogo} className="w-28 max-sm:h-[35px]" alt="Agro Market AMS" />
                        ) : (
                            <img src={amsFavIcon} className="w-11 max-sm:h-11" alt="Agro Market AMS" />
                        )}
                    </Link>

                    <span
                        className={`material-symbols-outlined text-base max-sm:border border-white bg-primary/90 py-[1px] px-[5px] cursor-pointer text-white font-normal rounded-full absolute -right-3 top-[19px] ${
                            sideBarOpen && 'rotate-180'
                        }`}
                        onClick={() => {
                            setSideBarOpen((prev) => !prev);
                        }}
                    >
                        keyboard_double_arrow_right
                    </span>
                </div>

                <div className={`${sideBarOpen && 'max-sm:col-span-2'} flex items-center pr-3 475:pr-6`}>
                    {hub && hubUser ? (
                        <>
                            {user?.hubs?.length < 2 || user?.hub ? (
                                <h3 className="ml-5 475:ml-8 max-md:leading-[22px] text-[19px] md:text-xl font-medium translate-y-1 font-rubik mr-3">
                                    {convertFromSlug(hub)}
                                </h3>
                            ) : (
                                <Dropdown
                                    dropdown={
                                        <div className="flex items-center">
                                            <h3 className="ml-4 475:ml-7 max-md:leading-[22px] text-[19px] md:text-xl font-medium translate-y-1 font-rubik mr-2">
                                                {convertFromSlug(hub)}
                                            </h3>
                                            <span className="material-symbols-outlined text-base 375:text-xl 700:text-base md:text-2xl translate-y-0.5">
                                                swap_vert
                                            </span>
                                        </div>
                                    }
                                    dropdownContainerClasses={
                                        '!w-[188px] mt-3 !border-0 shadow-dropdownShadow rounded left-0.5'
                                    }
                                    name={'Change Hub'}
                                    value={convertFromSlug(hub)}
                                    handleChange={(_, value) => {
                                        window.location.href = `/hub/${convertToSlug(value)}/sales`;
                                        // navigate(`/hub/${convertToSlug(value)}/sales`);
                                    }}
                                    dropdownOptions={user.hubs.map((hub) => ({
                                        label: <span className="font-medium">{hub}</span>,
                                        value: hub,
                                    }))}
                                />
                            )}
                        </>
                    ) : (
                        ''
                    )}
                    <div
                        className="flex cursor-pointer relative z-50 ml-auto"
                        onClick={() => setOpenProfile((prev) => !prev)}
                    >
                        <Avatar
                            fullName={user?.fullName}
                            img={user?.photoURL}
                            className={'w-[42px] h-[42px] cursor-pointer !rounded-[5px] !mr-0 580:!mr-3.5'}
                        />

                        <div className="max-580:hidden">
                            <p className={`flex font-semibold text-[15px] ${!user?.role && 'translate-y-2.5'}`}>
                                {capitalizeWords(user?.fullName || '')}
                                <span
                                    className={`material-symbols-outlined text-[20px] font-bold ml-[5px] transition-transform duration-300 ${
                                        openProfile ? 'rotate-0' : 'rotate-180 relative bottom-0.5'
                                    }`}
                                >
                                    expand_less
                                </span>
                            </p>
                            <p className={`text-[12.4px] text-text-light font-semibold mt-[-2px] capitalize`}>
                                {user?.role}
                            </p>
                        </div>

                        {/* Profile Popup */}
                        <div
                            className={`rounded bg-white shadow-dropdownShadow w-max max-w-[190px] absolute z-50 right-0 top-[122%] transition-all duration-300 ease-in ${
                                openProfile ? 'translate-y-0 opacity-100' : 'invisible translate-y-[70px] opacity-0'
                            }`}
                        >
                            <Link
                                to={user?.role ? ROUTES.PROFILE : '#'}
                                className="580:hidden not-hover px-3.5 py-2 flex"
                            >
                                <span className="material-symbols-outlined text-lg mr-2 text-text-light/70">
                                    account_box
                                </span>
                                <div>
                                    <p className={`font-semibold text-[15px] ${!user?.role && 'translate-y-[3px]'}`}>
                                        {capitalizeWords(user?.fullName || '')}
                                    </p>
                                    <p className={`text-[12.4px] text-text-light font-semibold mt-[-2px]`}>
                                        {user?.role}
                                    </p>
                                </div>
                            </Link>

                            {user?.role && (
                                <Link
                                    to={ROUTES.PROFILE}
                                    className="max-580:hidden not-hover flex items-center w-full py-1.5 text-sm text-text-light font-medium px-3.5 transition-colors hover:bg-gray-100"
                                >
                                    <span className="material-symbols-outlined text-lg mr-2 text-text-light/70">
                                        account_box
                                    </span>
                                    My Profile
                                </Link>
                            )}

                            {isSuperAdmin && (
                                <p
                                    className="flex items-center w-full py-1.5 text-sm text-text-light font-medium px-3.5 transition-colors hover:bg-gray-100"
                                    onClick={() => setOpenRoleModal(true)}
                                >
                                    <span className="material-symbols-outlined text-lg mr-2 text-text-light/70">
                                        group_add
                                    </span>
                                    Assign Roles
                                </p>
                            )}

                            {dashboardType === DASHBOARD_TYPES.LOGISTICS && isSuperAdmin && (
                                <p
                                    className="flex items-center w-full py-1.5 text-sm text-text-light font-medium px-3.5 transition-colors hover:bg-gray-100"
                                    onClick={() => setOpenLogisticsRate(true)}
                                >
                                    <span className="material-symbols-outlined text-lg mr-2 text-text-light/70">
                                        toll
                                    </span>
                                    Set Logistics Rate
                                </p>
                            )}

                            {(isSuperAdmin || isFinance) && (
                                <>
                                    {Object.values(DASHBOARD_TYPES)
                                        .filter((value) => value !== dashboardType)
                                        .map((value) => {
                                            return (
                                                <p
                                                    className="flex items-center w-full py-1.5 text-sm text-text-light font-medium px-3.5 transition-colors hover:bg-gray-100"
                                                    onClick={() => {
                                                        saveToStorage(LOCAL_STORAGE_KEYS.DASHBOARD_TYPE, value);
                                                        window.dispatchEvent(new Event('storage'));
                                                    }}
                                                    key={value}
                                                >
                                                    <span className="material-symbols-outlined text-lg mr-2 text-text-light/70">
                                                        change_circle
                                                    </span>
                                                    Switch To {capitalizeFirstLetter(value)}
                                                </p>
                                            );
                                        })}
                                </>
                            )}

                            <p
                                className="flex items-center w-full py-1.5 text-sm text-ams-red font-medium border-t px-3.5 transition-colors hover:bg-gray-100"
                                onClick={logout}
                            >
                                <span className="material-symbols-outlined text-lg mr-2">logout</span> Logout
                            </p>
                        </div>
                    </div>
                </div>

                {(openProfile || openNotification) && (
                    <div
                        className="fixed w-screen h-screen left-0 top-0 z-40 cursor-default"
                        onClick={() => {
                            setOpenNotification(false);
                            setOpenProfile(false);
                        }}
                    />
                )}
            </nav>

            {openRoleModal && (
                <Modal showCloseIcon={false}>
                    <AssignRoles closeModal={() => setOpenRoleModal(false)} />
                </Modal>
            )}

            {openLogisticsRate && (
                <Modal showCloseIcon={false}>
                    <SetLogisticsRate
                        closeModal={() => setOpenLogisticsRate(false)}
                        logisticsRate={logisticsRate}
                        setLogisticsRate={setLogisticsRate}
                    />
                </Modal>
            )}
        </>
    );
};

const AssignRoles = ({ closeModal }) => {
    const [usersLoading, setUsersLoading] = useState(true);
    const { users: storeUsers } = useSelector((state) => state.app);
    const users = storeUsers?.filter((user) => user.role !== 'super admin' && (user.verified || user.role));

    useEffect(() => {
        // We need to update this flow to allow for the 20 per query limit
        // So the UI needs to allow load 20 first and it should also allow searching by name.
        // So a little revamp
        fetchCollectionToRedux('users', setUsers, setUsersLoading, null);
    }, []);

    return (
        <div
            className={`rounded-lg w-[90vw] sm:w-[550px] bg-white relative ${
                !users?.length || usersLoading ? 'grid place-items-center h-[45vh] sm:h-[87vh]' : 'pt-7 pb-4'
            }`}
        >
            <span
                className="material-symbols-outlined text-text-light cursor-pointer font-semibold absolute top-2 right-2"
                onClick={closeModal}
            >
                close
            </span>

            {usersLoading ? (
                <Loader />
            ) : (
                <>
                    {users?.length ? (
                        <Table
                            tableClassName={'!w-full'}
                            className={'grid-cols-[265px_170px_70px] px-0.5 sm:px-2.5'}
                            titles={[{ title: 'User Full Name' }, { title: 'Current Role' }, { title: 'Action' }]}
                        >
                            <div className="h-[55vh] sm:h-[70vh] max-sm:overflow-y-auto">
                                {users.map((user, idx) => (
                                    <User
                                        idx={idx}
                                        users={users}
                                        key={user.id}
                                        user={user}
                                        moreThanOne={users.length > 1}
                                    />
                                ))}
                            </div>
                        </Table>
                    ) : (
                        <NotFound className={'-translate-y-9'} message="No Users Found." />
                    )}
                </>
            )}
        </div>
    );
};

const SetLogisticsRate = ({ closeModal, logisticsRate, setLogisticsRate }) => {
    const [saving, setSaving] = useState(false);
    const [baseKm, setBaseKm] = useState(logisticsRate?.baseKm);
    const [price, setPrice] = useState(logisticsRate?.price);

    const saveRate = async (evt) => {
        evt.preventDefault();
        if (baseKm === logisticsRate?.baseKm && price === logisticsRate?.price) {
            closeModal();
            return;
        }

        setSaving(true);
        try {
            const baseData = {
                baseKm: +baseKm,
                price: +price,
            };
            await setDoc(
                doc(db, 'logisticsRate', 'logisticsRate'),
                {
                    ...baseData,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            setLogisticsRate(baseData);
            toast.success('Logistics rate saved');
            closeModal();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={saveRate} className={`w-screen sm:w-[480px] rounded-lg bg-white overflow-hidden`}>
            <p className="text-lg font-bold px-5 py-3">
                Logistics Rate
                <span className="block text-sm font-normal mt-[1px]">Define base kilometer and price</span>
            </p>

            <div className="border-y px-5 grid grid-cols-2 gap-5 pt-5 pb-8 overflow-y-auto max-h-[350px] sm:max-h-[60vh]">
                <div className="max-sm:col-span-2">
                    <p className="text-sm font-medium">Base Kilometer</p>
                    <input
                        className="border w-full mt-1.5 placeholder:text-text-light placeholder:!text-[13px]"
                        placeholder="E.g., 1.5"
                        value={formatCurrency(baseKm || '')}
                        onChange={(e) => setBaseKm(e.target.value.replace(/,/g, ''))}
                        inputMode="decimal"
                        required
                        pattern="\d{1,3}(,\d{3})*(?:\.\d{1,2})?"
                        title="Only numbers allowed"
                    />
                </div>

                <div className="max-sm:col-span-2">
                    <p className="text-sm font-medium">Price</p>
                    <input
                        className="border w-full mt-1.5 placeholder:text-text-light placeholder:!text-[13px]"
                        placeholder="Enter price in ₦"
                        value={formatCurrency(price || '')}
                        onChange={(e) => setPrice(e.target.value.replace(/,/g, ''))}
                        required
                        pattern="\d{1,3}(,\d{3})*(?:\.\d{1,2})?"
                        title="Only numbers allowed"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 h-[50px]">
                <button
                    type="button"
                    onClick={closeModal}
                    className="font-semibold text-[15px] transition-colors saturate-200 text-ams-red bg-white hover:bg-ams-red hover:text-white border-r"
                >
                    Cancel
                </button>
                <button className="font-semibold text-[15px] transition-colors duration-200 bg-white text-primary hover:bg-primary hover:text-white">
                    {saving ? <Loader className={'my-0.5'} /> : 'Save'}
                </button>
            </div>
        </form>
    );
};

const User = ({ user, moreThanOne, users, idx }) => {
    const [updateLoading, setUpdateLoading] = useState(false);
    const { hubs } = useSelector((state) => state.app);
    const dispatch = useDispatch();

    const updateRole = async (role) => {
        try {
            const batch = writeBatch(db);
            setUpdateLoading(true);

            const userRef = doc(db, 'users', user.id);
            const updateData = { role };

            // Legacy compatibility: if removing a role from a user who had one, mark them as verified
            // so they remain visible in the user management system
            if (!role && user.role) {
                updateData.verified = true;
            }

            const removingRegionalHead = role !== HUB_ROLES.MANAGER && user.role === HUB_ROLES.MANAGER;
            const removingHubAgent = role !== HUB_ROLES.AGENT && user.role === HUB_ROLES.AGENT;

            if (removingRegionalHead) {
                const proceed = user?.hubs?.length
                    ? window.confirm(
                          `Removing ${user.fullName} as Regional Head will also remove their access to all hubs. Sure to proceed?`
                      )
                    : true;
                if (!proceed) return;
                updateData.hubs = null;
            }

            if (removingHubAgent) {
                const proceed = user?.hub
                    ? window.confirm(
                          `Removing ${user.fullName} as Hub Agent will also remove their hub access. Sure to proceed?`
                      )
                    : true;
                if (!proceed) return;
                updateData.hub = null;
            }

            batch.update(userRef, { ...updateData, updatedAt: serverTimestamp() });

            let currentManagerHubIDs;
            if (removingRegionalHead) {
                currentManagerHubIDs = await removeManagerFromHubs(batch);
            }

            let hubAgents;
            if (removingHubAgent) {
                hubAgents = await removeUserFromHub(batch);
            }
            await batch.commit();

            if (currentManagerHubIDs?.length) removeManagerFromHubsRedux(currentManagerHubIDs);
            if (hubAgents?.length) removeUserFromHubRedux(hubAgents);

            const userSnapshot = await getDoc(userRef);
            const _user = {
                ...userSnapshot.data(),
                ...formatDateValues(userSnapshot.data()),
            };

            // Update the local users array
            users[idx] = _user;
            // Create a new array to ensure React re-renders and Redux state updates properly
            const updatedUsers = [...users];
            dispatch(setUsers(updatedUsers));
        } catch (error) {
            toast.error(`Request failed. Please update ${capitalizeWords(user.fullName)} role again.`);
        } finally {
            setUpdateLoading(false);
        }
    };

    const removeManagerFromHubs = async (batch) => {
        const currentManagerHubIDs = (
            await fetchCollectionByQueryLocal(query(collection(db, 'hubs'), where('manager.value', '==', user.id)))
        ).map(({ id }) => id);

        if (currentManagerHubIDs?.length) {
            currentManagerHubIDs.forEach((hubId) =>
                batch.update(doc(db, 'hubs', hubId), { manager: null, updatedAt: serverTimestamp() })
            );
        }
        return currentManagerHubIDs;
    };

    const removeManagerFromHubsRedux = (currentManagerHubIDs) => {
        if (window.location.pathname === ROUTES.HUBS) {
            const hubsArr = [...hubs];
            currentManagerHubIDs.forEach((id) => {
                const oldHubIdx = hubsArr.findIndex((hub) => id === hub.id);
                hubsArr[oldHubIdx] = { ...hubsArr[oldHubIdx], manager: null };
            });
            dispatch(setHubs(hubsArr));
        }
    };

    const removeUserFromHub = async (batch) => {
        const [hub] = await fetchCollectionByQueryLocal(query(collection(db, 'hubs'), where('name', '==', user.hub)));
        if (hub) {
            batch.update(doc(db, 'hubs', hub.id), {
                agents: hub.agents.filter(({ value }) => value !== user.id),
                updatedAt: serverTimestamp(),
            });
            return hub.agents;
        }
    };

    const removeUserFromHubRedux = (agents) => {
        if (window.location.pathname === ROUTES.HUBS) {
            const hubsArr = [...hubs];
            const oldHubIdx = hubsArr.findIndex((hub) => user.hub === hub.name);
            hubsArr[oldHubIdx] = { ...hubsArr[oldHubIdx], agents: agents.filter(({ value }) => value !== user.id) };
            dispatch(setHubs(hubsArr));
        }
    };

    return (
        <TableItem className={'grid-cols-[265px_170px_70px] px-0.5 sm:px-2.5'} key={user.id} moreThanOne={moreThanOne}>
            <td className="flex items-center capitalize">
                <Avatar img={user?.photoURL} fullName={user.fullName} />
                {user.fullName}
            </td>
            <td className="capitalize">{user?.role ?? 'N/A'}</td>
            <td>
                {updateLoading ? (
                    <Loader />
                ) : (
                    <Dropdown
                        dropdown={<span className="material-symbols-outlined">manage_accounts</span>}
                        dropdownContainerClasses={'!w-[188px] mt-1 !border-0 shadow-dropdownShadow rounded -right-3'}
                        name={'UpdateRole'}
                        handleChange={(_, value) => updateRole(value ? value.toLowerCase() : null)}
                        dropdownOptions={[
                            ...DEPARTMENTS,
                            capitalizeWords(HUB_ROLES.MANAGER),
                            user?.role ? 'remove access' : '',
                        ]
                            .filter((dept) => dept && dept.toLowerCase() !== user?.role && dept !== 'Super Admin')
                            .map((dept) => ({
                                label: dept.startsWith('remove') ? (
                                    <span className="text-red-500">{capitalizeWords(dept)}</span>
                                ) : (
                                    dept
                                ),
                                value: dept === 'remove access' ? null : dept,
                            }))}
                    />
                )}
            </td>
        </TableItem>
    );
};
